import { useCallback } from 'react';
import { BigNumber } from 'ethers';
import { Skeleton, Text } from '@cronos-labs/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk';
import { useRouter } from 'next/router';

import { formatMarketsSummaryUsdValue } from '@lib/units';
import { getNormalizedUsdPrice, getUnderlyingUsdValue } from '@lib/math';
import useAppSelector from '@hooks/useAppSelector';
import ModeSwitch from '@components/ModeSwitch';
import XTonicBoost from '@components/XTonicBoost/XTonicBoost';
import useSdkAndSupportedAssets from '@hooks/useSdkAndSupportedAssets';

import MarketsSummary from './MarketsSummary';
import { modes, Mode } from './types';

interface Props {
  mode: Mode;
}

export default function Header({ mode }: Props) {
  const { push } = useRouter();

  const { list: supportedAssets } = useSdkAndSupportedAssets('MAIN');
  const { list: supportedVENOPoolAssets } = useSdkAndSupportedAssets('VENO');
  const { list: supportedDeFiPoolAssets } = useSdkAndSupportedAssets('DEFI');

  const onModeChange = (m: Mode) => {
    if (m === 'MAIN') {
      push('/markets');
    } else if (m === 'VENO') {
      push('/markets/veno');
    } else if (m === 'DEFI') {
      push('/markets/defi');
    }
  };

  const {
    borrowAmounts,
    supplyAmounts,
    borrowAmountsOfDeFiPool,
    supplyAmountsOfDeFiPool,
    borrowAmountsOfLCROPool,
    supplyAmountsOfLCROPool,
    usdPrices,
  } = useAppSelector((state) => ({
    borrowAmounts: state.mainMarkets.borrowAmounts,
    supplyAmounts: state.mainMarkets.supplyAmounts,
    borrowAmountsOfDeFiPool: state.defiMarkets.borrowAmounts,
    supplyAmountsOfDeFiPool: state.defiMarkets.supplyAmounts,
    borrowAmountsOfLCROPool: state.venoMarkets.borrowAmounts,
    supplyAmountsOfLCROPool: state.venoMarkets.supplyAmounts,
    usdPrices: state.usdPrices.bySymbol,
  }));

  const getBorrowSupplyInUsd = useCallback(
    (
      amounts: Record<string, string | undefined>,
      supportedAssetsLists: TectonicAsset[]
    ) => {
      if (
        Object.keys(amounts).length < supportedAssetsLists.length ||
        Object.keys(usdPrices).length < supportedAssetsLists.length
      ) {
        return null;
      }

      return supportedAssetsLists.reduce((accumulator, asset) => {
        const assetAmount = amounts[asset.symbol];
        const assetUsdPrice = usdPrices[asset.symbol];

        if (assetAmount && assetUsdPrice) {
          const assetUsdValue = getNormalizedUsdPrice(
            asset,
            getUnderlyingUsdValue(
              asset,
              BigNumber.from(assetAmount),
              BigNumber.from(assetUsdPrice)
            )
          );

          return accumulator.add(assetUsdValue);
        }

        return accumulator;
      }, BigNumber.from(0));
    },
    [usdPrices]
  );

  const getAllPoolTotalInUsd = useCallback(
    (
      mainPoolInUsd: Null<BigNumber>,
      LCROPoolInUsd: Null<BigNumber>,
      defiPoolInUsd: Null<BigNumber>
    ) =>
      (mainPoolInUsd ?? BigNumber.from(0))
        .add(LCROPoolInUsd ?? BigNumber.from(0))
        .add(defiPoolInUsd ?? BigNumber.from(0)),
    []
  );

  const totalBorrowInUsdOfDeFiPool: Null<BigNumber> = getBorrowSupplyInUsd(
    borrowAmountsOfDeFiPool,
    supportedDeFiPoolAssets
  );

  const totalSupplyInUsdOfDeFiPool: Null<BigNumber> = getBorrowSupplyInUsd(
    supplyAmountsOfDeFiPool,
    supportedDeFiPoolAssets
  );

  const totalBorrowInUsdOfLCROPool: Null<BigNumber> = getBorrowSupplyInUsd(
    borrowAmountsOfLCROPool,
    supportedVENOPoolAssets
  );

  const totalSupplyInUsdOfLCROPool: Null<BigNumber> = getBorrowSupplyInUsd(
    supplyAmountsOfLCROPool,
    supportedVENOPoolAssets
  );

  const totalBorrowInUsd: Null<BigNumber> = getBorrowSupplyInUsd(
    borrowAmounts,
    supportedAssets
  );

  const totalSupplyInUsd: Null<BigNumber> = getBorrowSupplyInUsd(
    supplyAmounts,
    supportedAssets
  );

  const allPoolTotalBorrowInUsd = getAllPoolTotalInUsd(
    totalBorrowInUsd,
    totalBorrowInUsdOfLCROPool,
    totalBorrowInUsdOfDeFiPool
  );

  const allPoolTotalSupplyInUsd = getAllPoolTotalInUsd(
    totalSupplyInUsd,
    totalSupplyInUsdOfLCROPool,
    totalSupplyInUsdOfDeFiPool
  );
  return (
    <>
      <MarketsSummary
        isTopBanner
        totalBorrow={
          allPoolTotalBorrowInUsd ? (
            <Text className="text-medium font-semibold">
              {formatMarketsSummaryUsdValue(allPoolTotalBorrowInUsd)}
            </Text>
          ) : (
            <Skeleton className="w-1/2">
              <Text className="text-medium text-transparent">.</Text>
            </Skeleton>
          )
        }
        totalSupply={
          allPoolTotalSupplyInUsd ? (
            <Text className="text-medium font-semibold">
              {formatMarketsSummaryUsdValue(allPoolTotalSupplyInUsd)}
            </Text>
          ) : (
            <Skeleton className="w-1/2">
              <Text className="text-medium text-transparent">.</Text>
            </Skeleton>
          )
        }
      />
      <XTonicBoost />
      <ModeSwitch
        className="!mb-3 mt-5 !ml-0 max-h-[48px] gap-4 desktop:!justify-center tablet:!justify-center mobile:!justify-start"
        modes={modes}
        variant="tabs"
        mode={mode}
        onModeChange={onModeChange}
      />
    </>
  );
}
